// SettingsPage.tsx — admin-only post-setup configuration. Hosts OAuth provider
// creds, the test-JWT issuer, and the theme designer as sibling cards on a
// single page so the navbar stays uncluttered.
import { useCallback, useEffect, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Spinner from 'react-bootstrap/Spinner'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { BsCheck, BsClipboard, BsPlusLg, BsSave, BsTrash } from 'react-icons/bs'
import Text from 'common/ui/Text'
import { useConfirm } from 'common/ui/ConfirmDialog'
import { palette } from 'common'
import { apiFetch, useAuth } from '../hooks/useAuth'
import { AUTHZ_PRESET } from '../theme'

const { ThemePicker } = palette

interface OAuthCreds {
    client_id: string
    client_secret: string
}

interface ProviderConfig {
    key: 'google' | 'github'
    label: string
    path: string
    redirectHint: string
    clientIdPlaceholder: string
    clientSecretPlaceholder: string
    description: string
}

const PROVIDERS: ProviderConfig[] = [
    {
        key: 'google',
        label: 'Google OAuth',
        path: 'prod/social/google/credentials',
        redirectHint: '/api/auth/google/callback',
        clientIdPlaceholder: '123-abc.apps.googleusercontent.com',
        clientSecretPlaceholder: 'GOCSPX-…',
        description: 'Create a Web application credential in the Google Cloud Console and add the redirect URL below to its authorized redirect URIs.',
    },
    {
        key: 'github',
        label: 'GitHub OAuth',
        path: 'prod/social/github/credentials',
        redirectHint: '/api/auth/github/callback',
        clientIdPlaceholder: 'Iv1.…',
        clientSecretPlaceholder: 'github_pat_… or client secret',
        description: 'Register an OAuth App at github.com/settings/developers and set the Authorization callback URL to the redirect URL below.',
    },
]

interface ProviderState {
    clientId: string
    clientSecret: string
    loaded: boolean
    exists: boolean
}

const emptyState = (): ProviderState => ({ clientId: '', clientSecret: '', loaded: false, exists: false })

const SettingsPage = () => {
    const { email, roles, scopes, loading, error: authError } = useAuth()
    const isAdmin = scopes.includes('authz.admin')

    const [state, setState] = useState<Record<string, ProviderState>>({
        google: emptyState(),
        github: emptyState(),
    })
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [savingKey, setSavingKey] = useState<string | null>(null)
    const [origin, setOrigin] = useState('')

    // Test JWT card state
    const [jwtSubject, setJwtSubject] = useState('')
    const [jwtScopes, setJwtScopes] = useState('')
    const [jwtRoles, setJwtRoles] = useState('')
    const [jwtTtl, setJwtTtl] = useState('3600')
    const [jwtToken, setJwtToken] = useState('')
    const [jwtError, setJwtError] = useState('')
    const [jwtCopied, setJwtCopied] = useState(false)

    useEffect(() => { setOrigin(window.location.origin) }, [])

    useEffect(() => {
        if (loading) return
        if (email) setJwtSubject((prev) => prev || email)
        if (scopes.length) setJwtScopes((prev) => prev || scopes.join(', '))
        if (roles.length) setJwtRoles((prev) => prev || roles.join(', '))
    }, [loading, email, scopes, roles])

    const flash = (msg: string, isError = false) => {
        if (isError) { setError(msg); setSuccess('') } else { setSuccess(msg); setError('') }
        setTimeout(() => { setError(''); setSuccess('') }, 4000)
    }

    const encodePath = (p: string) => p.split('/').map(encodeURIComponent).join('/')

    const loadProvider = useCallback(async (p: ProviderConfig) => {
        const res = await apiFetch(`/api/secrets/${encodePath(p.path)}`)
        if (res.status === 404) {
            setState((prev) => ({ ...prev, [p.key]: { ...emptyState(), loaded: true } }))
            return
        }
        if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            setError(d.error || `Failed to load ${p.label} (${res.status})`)
            setState((prev) => ({ ...prev, [p.key]: { ...prev[p.key], loaded: true } }))
            return
        }
        const data = await res.json()
        let creds: OAuthCreds = { client_id: '', client_secret: '' }
        try {
            const parsed = JSON.parse(data.value || '{}')
            creds = {
                client_id: typeof parsed.client_id === 'string' ? parsed.client_id : '',
                client_secret: typeof parsed.client_secret === 'string' ? parsed.client_secret : '',
            }
        } catch {
            setError(`${p.label} secret exists but isn't valid JSON; saving will overwrite it.`)
        }
        setState((prev) => ({
            ...prev,
            [p.key]: {
                clientId: creds.client_id,
                clientSecret: creds.client_secret,
                loaded: true,
                exists: true,
            },
        }))
    }, [])

    useEffect(() => {
        if (loading || !email || !isAdmin) return
        PROVIDERS.forEach((p) => { void loadProvider(p) })
    }, [loading, email, isAdmin, loadProvider])

    const handleChange = (key: 'google' | 'github', field: 'clientId' | 'clientSecret', value: string) => {
        setState((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }))
    }

    const handleSave = async (p: ProviderConfig) => {
        const s = state[p.key]
        if (!s.clientId || !s.clientSecret) {
            flash(`${p.label}: client ID and client secret are both required`, true)
            return
        }
        setSavingKey(p.key)
        const body = JSON.stringify({
            value: JSON.stringify({ client_id: s.clientId, client_secret: s.clientSecret }),
            description: `${p.label} client credentials`,
        })
        const res = await apiFetch(`/api/secrets/${encodePath(p.path)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body,
        })
        setSavingKey(null)
        if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            flash(d.error || `Save failed (${res.status})`, true)
            return
        }
        setState((prev) => ({ ...prev, [p.key]: { ...prev[p.key], exists: true } }))
        flash(`${p.label} saved. Changes take effect within ~30s (cached).`)
    }

    const [confirm, confirmDialog] = useConfirm()

    const handleClear = async (p: ProviderConfig) => {
        const ok = await confirm({
            title: 'Delete Credentials',
            body: <>Delete the <strong>{p.label}</strong> credentials? Social login through this provider stops working until you re-enter them.</>,
            confirmLabel: 'Delete',
        })
        if (!ok) return
        const res = await apiFetch(`/api/secrets/${encodePath(p.path)}`, { method: 'DELETE' })
        if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            flash(d.error || `Delete failed (${res.status})`, true)
            return
        }
        setState((prev) => ({ ...prev, [p.key]: { ...emptyState(), loaded: true } }))
        flash(`${p.label} credentials cleared.`)
    }

    const handleGenerateJwt = async (e: React.FormEvent) => {
        e.preventDefault()
        setJwtError('')
        setJwtToken('')
        setJwtCopied(false)
        try {
            const res = await apiFetch('/api/me/test-jwt', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    subject: jwtSubject || undefined,
                    scopes: jwtScopes ? jwtScopes.split(',').map((s) => s.trim()).filter(Boolean) : [],
                    roles: jwtRoles ? jwtRoles.split(',').map((s) => s.trim()).filter(Boolean) : [],
                    expires_in_seconds: parseInt(jwtTtl) || 3600,
                }),
            })
            if (!res.ok) {
                const data = await res.json()
                throw new Error(data.error || 'Failed to generate token')
            }
            const data = await res.json()
            setJwtToken(data.token)
        } catch (err: unknown) {
            setJwtError(err instanceof Error ? err.message : 'Failed to generate token')
        }
    }

    const copyJwt = async () => {
        await navigator.clipboard.writeText(jwtToken)
        setJwtCopied(true)
        setTimeout(() => setJwtCopied(false), 2000)
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <Alert variant="warning">
                Settings is admin-only. You need the <code>authz.admin</code>{' '}
                scope to manage these.
            </Alert>
        )
    }

    return (
        <>
            <h1 className="mb-1">Settings</h1>
            <p className="text-muted mb-4">
                Post-setup configuration. OAuth credentials are stored encrypted in the vault
                and read by the social-login handlers; existing values are pre-filled below.
            </p>

            {authError && <Alert variant="danger">{authError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Row className="g-3 mb-3">
            {PROVIDERS.map((p) => {
                const s = state[p.key]
                const callback = origin ? `${origin}${p.redirectHint}` : p.redirectHint
                return (
                    <Col key={p.key} md={6} className="d-flex">
                    <Card className="flex-fill">
                        <Card.Header className="d-flex align-items-center justify-content-between">
                            <span>{p.label}</span>
                            <Badge bg={s.exists ? 'info' : 'secondary'}>
                                {s.loaded ? (s.exists ? 'configured' : 'not configured') : 'loading…'}
                            </Badge>
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted small mb-2">{p.description}</p>
                            <div className="text-muted small mb-3">
                                <span>Redirect URL: </span>
                                <code className="text-info">{callback}</code>
                            </div>
                            <Form.Group className="mb-3" controlId={`${p.key}-cid`}>
                                <Form.Label>Client ID</Form.Label>
                                <Text
                                    type="text"
                                    style={{ width: "100%" }}
                                    value={s.clientId}
                                    onChange={(e) => handleChange(p.key, 'clientId', e.target.value)}
                                    placeholder={p.clientIdPlaceholder}
                                    disabled={!s.loaded}
                                />
                            </Form.Group>
                            <Form.Group controlId={`${p.key}-csec`}>
                                <Form.Label>Client Secret</Form.Label>
                                <Text
                                    type="password"
                                    style={{ width: "100%" }}
                                    value={s.clientSecret}
                                    onChange={(e) => handleChange(p.key, 'clientSecret', e.target.value)}
                                    placeholder={p.clientSecretPlaceholder}
                                    disabled={!s.loaded}
                                />
                            </Form.Group>
                        </Card.Body>
                        <Card.Footer className="d-flex align-items-center justify-content-between gap-3">
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    onClick={() => handleSave(p)}
                                    disabled={!s.loaded || savingKey === p.key}
                                    className="d-flex align-items-center gap-2"
                                >
                                    <BsSave /> {savingKey === p.key ? 'Saving…' : (s.exists ? 'Update' : 'Save')}
                                </Button>
                                {s.exists && (
                                    <Button
                                        variant="outline-danger"
                                        onClick={() => handleClear(p)}
                                        disabled={savingKey === p.key}
                                        className="d-flex align-items-center gap-2"
                                    >
                                        <BsTrash /> Clear
                                    </Button>
                                )}
                            </div>
                            <span className="text-muted small">
                                Stored at <code>{p.path}</code>
                            </span>
                        </Card.Footer>
                    </Card>
                    </Col>
                )
            })}
            </Row>

            <Row className="g-3 mb-3">
            <Col md={6} className="d-flex">
            {/* Minting a bearer token is the one genuinely dangerous thing on
                this page, and it used to say so with border="danger". C-CARD-4
                removes card borders, and with the width zeroed that prop set a
                colour on an edge that no longer renders — the warning was
                already invisible. Moved into the header, where it reads. */}
            <Card className="flex-fill">
                <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                    <span>Issue JWT</span>
                    <Badge bg="danger">sensitive</Badge>
                </Card.Header>
                <Card.Body>
                    {jwtError && <Alert variant="danger">{jwtError}</Alert>}
                    <Form id="test-jwt-form" onSubmit={handleGenerateJwt}>
                        <Form.Group className="mb-3" controlId="jwt-subject">
                            <Form.Label>Subject</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder={email || 'user@example.com'}
                                value={jwtSubject}
                                onChange={(e) => setJwtSubject(e.target.value)}
                            />
                            <Form.Text muted>Defaults to your email if blank.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="jwt-scopes">
                            <Form.Label>Scopes</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="identity.users.read, ..."
                                value={jwtScopes}
                                onChange={(e) => setJwtScopes(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="jwt-roles">
                            <Form.Label>Roles</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="admin, developer"
                                value={jwtRoles}
                                onChange={(e) => setJwtRoles(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="jwt-ttl">
                            <Form.Label>TTL (seconds)</Form.Label>
                            <Text
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={jwtTtl}
                                onChange={(e) => setJwtTtl(e.target.value.replace(/[^0-9]/g, ''))}
                                style={{ maxWidth: '12rem' }}
                            />
                        </Form.Group>
                    </Form>

                    {jwtToken && (
                        <div className="mt-3">
                            <Form.Label htmlFor="jwt-output">Generated Token</Form.Label>
                            <Form.Control
                                id="jwt-output"
                                as="textarea"
                                rows={4}
                                readOnly
                                value={jwtToken}
                                style={{ fontSize: '0.85rem' }}
                            />
                            <div className="mt-2">
                                <Button variant="secondary" onClick={copyJwt} className="d-flex align-items-center gap-2">
                                    {jwtCopied ? <><BsCheck /> Copied</> : <><BsClipboard /> Copy</>}
                                </Button>
                            </div>
                        </div>
                    )}
                </Card.Body>
                <Card.Footer>
                    <Button variant="danger" type="submit" form="test-jwt-form" className="d-flex align-items-center gap-2">
                        <BsPlusLg /> Generate
                    </Button>
                </Card.Footer>
            </Card>
            </Col>
            </Row>

            <h2 className="h5 mt-4 mb-2">Theme</h2>
            <p className="text-muted small mb-3">
                Pick a starting palette, lock the colors you care about, and drag
                any swatch to run gradient descent on the remaining roles. The
                loss function balances WCAG contrast targets against saturation
                caps, semantic-hue families, and gamut limits. Pick a
                <strong> Vision</strong> model to solve for a form of color
                blindness instead: contrast is then measured on the colors that
                viewer sees, and colors meaning different things have to stay
                tellable apart. Press
                <strong> Apply</strong> to write the result to this browser.
            </p>
            <ThemePicker
                extraPresets={[AUTHZ_PRESET]}
                defaultPresetId="authz"
            />

            {confirmDialog}
        </>
    )
}

export default SettingsPage
